import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { CustomsBrokerDto, CustomsBrokerInput } from '@crm/shared'
import { createCustomsBroker, updateCustomsBroker } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'
import { CUSTOMS_BROKERS_KEY } from './useCustomsBrokers'

interface Props {
  /** Правка карточки; без него — новый брокер. */
  broker?: CustomsBrokerDto
  /** Только просмотр (ADMIN, ГД, РОЗ): поля без правки. */
  readOnly?: boolean
  /** Подставить в название (набрали в поиске выпадашки). */
  initialName?: string
  onClose: () => void
  onSaved?: (broker: CustomsBrokerDto) => void
}

type Form = Required<{ [K in keyof CustomsBrokerInput]: string }>

const FIELDS: Array<{ key: keyof Form; label: string; placeholder: string; multiline?: boolean }> =
  [
    { key: 'name', label: 'Название', placeholder: 'Например: ООО «Брокер»' },
    { key: 'contactPerson', label: 'Контактное лицо', placeholder: 'Имя, должность' },
    { key: 'phone', label: 'Телефон', placeholder: '+7 …' },
    { key: 'email', label: 'E-mail', placeholder: 'можно несколько через запятую' },
    {
      key: 'note',
      label: 'Примечание',
      placeholder: 'Условия, сроки ответа, что умеет',
      multiline: true,
    },
  ]

const MAX: Record<keyof Form, number> = {
  name: 200,
  contactPerson: 200,
  phone: 100,
  email: 200,
  note: 2000,
}

/**
 * Карточка таможенного брокера: название (обязательно) и по желанию контакты и
 * примечание — справочник наполняется по ходу работы. Скрыть брокера — на
 * странице «Брокеры»; старые запросы остаются с ним.
 */
export function BrokerCardModal({ broker, readOnly, initialName, onClose, onSaved }: Props) {
  const qc = useQueryClient()
  const [form, setForm] = useState<Form>({
    name: broker?.name ?? initialName ?? '',
    contactPerson: broker?.contactPerson ?? '',
    phone: broker?.phone ?? '',
    email: broker?.email ?? '',
    note: broker?.note ?? '',
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, saving])

  const submit = async () => {
    if (readOnly || saving) return
    if (!form.name.trim()) {
      setError('Укажите название брокера')
      return
    }
    setSaving(true)
    setError('')
    const dto = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v.trim() || null]),
    ) as CustomsBrokerInput
    try {
      const saved = broker
        ? await updateCustomsBroker(broker.id, dto)
        : await createCustomsBroker({ ...dto, name: form.name.trim() })
      void qc.invalidateQueries({ queryKey: [...CUSTOMS_BROKERS_KEY] })
      toast.success(broker ? 'Карточка брокера сохранена' : `Брокер «${saved.name}» добавлен`)
      onSaved?.(saved)
      onClose()
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось сохранить карточку брокера'))
      setSaving(false)
    }
  }

  const title = broker ? (readOnly ? 'Брокер' : 'Карточка брокера') : 'Новый брокер'
  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-700'

  // Портал: окно открывают и из другого окна («Отправить брокеру») — встаёт поверх.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label={title}
        className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            disabled={saving}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs text-gray-500 mb-1" htmlFor={`broker-${f.key}`}>
                {f.label}
                {f.key === 'name' && !readOnly && <span className="text-red-500"> *</span>}
              </label>
              {f.multiline ? (
                <textarea
                  id={`broker-${f.key}`}
                  value={form[f.key]}
                  maxLength={MAX[f.key]}
                  rows={3}
                  disabled={readOnly}
                  placeholder={readOnly ? '' : f.placeholder}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className={`${inputClass} resize-y`}
                />
              ) : (
                <input
                  id={`broker-${f.key}`}
                  autoFocus={f.key === 'name' && !readOnly}
                  value={form[f.key]}
                  maxLength={MAX[f.key]}
                  disabled={readOnly}
                  placeholder={readOnly ? '' : f.placeholder}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void submit()
                    }
                  }}
                  className={inputClass}
                />
              )}
            </div>
          ))}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {readOnly ? 'Закрыть' : 'Отмена'}
          </button>
          {!readOnly && (
            <button
              onClick={() => void submit()}
              disabled={saving}
              className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Сохранение…' : broker ? 'Сохранить' : 'Добавить'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
